<script setup>
import { computed } from 'vue';
import { getLanguageName } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import { dynamicTime } from 'shared/helpers/timeHelper';
import ContactDetailsItem from './ContactDetailsItem.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';

const props = defineProps({
  conversationAttributes: {
    type: Object,
    default: () => ({}),
  },
  contactAttributes: {
    type: Object,
    default: () => ({}),
  },
  assignee: {
    type: Object,
    default: null,
  },
  status: {
    type: String,
    default: '',
  },
  createdAt: {
    type: Number,
    default: null,
  },
  lastActivityAt: {
    type: Number,
    default: null,
  },
});

const referer = computed(() => props.conversationAttributes.referer);
const initiatedAt = computed(
  () => props.conversationAttributes.initiated_at?.timestamp
);

const browserInfo = computed(() => props.conversationAttributes.browser);

const browserName = computed(() => {
  if (!browserInfo.value) return '';
  const { browser_name: name = '', browser_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const browserLanguage = computed(() =>
  getLanguageName(props.conversationAttributes.browser_language)
);

const platformName = computed(() => {
  if (!browserInfo.value) return '';
  const { platform_name: name = '', platform_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const createdAtIp = computed(() => props.contactAttributes.created_at_ip);

const assigneeName = computed(() => {
  if (!props.assignee) return null;
  return props.assignee.name || props.assignee.available_name;
});

const assigneeThumbnail = computed(() => {
  if (!props.assignee) return '';
  return props.assignee.thumbnail;
});

const conversationStatus = computed(() => {
  const statusMap = {
    open: {
      label: 'CONTACT_PANEL.CONVERSATION_STATUS.OPEN',
      class: 'bg-green-100 text-green-800',
      icon: 'i-lucide-circle-dot',
    },
    resolved: {
      label: 'CONTACT_PANEL.CONVERSATION_STATUS.RESOLVED',
      class: 'bg-blue-100 text-blue-800',
      icon: 'i-lucide-check-circle-2',
    },
    pending: {
      label: 'CONTACT_PANEL.CONVERSATION_STATUS.PENDING',
      class: 'bg-yellow-100 text-yellow-800',
      icon: 'i-lucide-clock',
    },
    snoozed: {
      label: 'CONTACT_PANEL.CONVERSATION_STATUS.SNOOZED',
      class: 'bg-purple-100 text-purple-800',
      icon: 'i-lucide-moon',
    },
  };
  return statusMap[props.status] || null;
});

const formattedCreatedAt = computed(() => {
  if (!props.createdAt) return '';
  const timestamp = props.createdAt;
  const timestampInSeconds = 
    timestamp > 10000000000 ? Math.floor(timestamp / 1000) : timestamp;
  return dynamicTime(timestampInSeconds);
});

const formattedLastActivityAt = computed(() => {
  if (!props.lastActivityAt) return '';
  const timestamp = props.lastActivityAt;
  const timestampInSeconds = 
    timestamp > 10000000000 ? Math.floor(timestamp / 1000) : timestamp;
  return dynamicTime(timestampInSeconds);
});

const staticElements = computed(() =>
  [
    {
      content: initiatedAt,
      title: 'CONTACT_PANEL.INITIATED_AT',
      key: 'static-initiated-at',
      type: 'static_attribute',
    },
    {
      content: browserLanguage,
      title: 'CONTACT_PANEL.BROWSER_LANGUAGE',
      key: 'static-browser-language',
      type: 'static_attribute',
    },
    {
      content: referer,
      title: 'CONTACT_PANEL.INITIATED_FROM',
      key: 'static-referer',
      type: 'static_attribute',
    },
    {
      content: browserName,
      title: 'CONTACT_PANEL.BROWSER',
      key: 'static-browser',
      type: 'static_attribute',
    },
    {
      content: platformName,
      title: 'CONTACT_PANEL.OS',
      key: 'static-platform',
      type: 'static_attribute',
    },
    {
      content: createdAtIp,
      title: 'CONTACT_PANEL.IP_ADDRESS',
      key: 'static-ip-address',
      type: 'static_attribute',
    },
  ].filter(attribute => !!attribute.content.value)
);
</script>

<template>
  <div class="conversation--details">
    <div v-if="assigneeName" class="overflow-auto py-3 px-4">
      <div class="items-center flex justify-between mb-1.5">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('CONTACT_PANEL.CONVERSATION_ASSIGNEE') }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <Thumbnail
          :src="assigneeThumbnail"
          :username="assigneeName"
          size="32px"
        />
        <span class="text-sm text-n-slate-11">{{ assigneeName }}</span>
      </div>
    </div>
    <div v-if="conversationStatus" class="overflow-auto py-3 px-4">
      <div class="items-center flex justify-between mb-1.5">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('CONTACT_PANEL.CONVERSATION_STATUS.LABEL') }}
        </span>
      </div>
      <div class="inline-flex items-center gap-1.5">
        <span :class="conversationStatus.icon" class="text-base" />
        <span
          class="inline-flex px-2 py-0.5 text-xs font-medium rounded-full"
          :class="conversationStatus.class"
        >
          {{ $t(conversationStatus.label) }}
        </span>
      </div>
    </div>
    <div v-if="formattedCreatedAt" class="overflow-auto py-3 px-4">
      <div class="items-center flex justify-between mb-1.5">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('CONTACT_PANEL.CONVERSATION_CREATION') }}
        </span>
      </div>
      <div class="text-sm text-n-slate-11">
        {{ formattedCreatedAt }}
      </div>
    </div>
    <div v-if="formattedLastActivityAt" class="overflow-auto py-3 px-4">
      <div class="items-center flex justify-between mb-1.5">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('CONTACT_PANEL.LAST_ACTIVITY') }}
        </span>
      </div>
      <div class="text-sm text-n-slate-11">
        {{ formattedLastActivityAt }}
      </div>
    </div>
    <CustomAttributes
      :static-elements="staticElements"
      attribute-class="conversation--attribute"
      attribute-from="conversation_panel"
      attribute-type="conversation_attribute"
    >
      <template #staticItem="{ element }">
        <ContactDetailsItem
          :key="element.title"
          :title="$t(element.title)"
          :value="element.content.value"
        >
          <a
            v-if="element.key === 'static-referer'"
            :href="element.content.value"
            rel="noopener noreferrer nofollow"
            target="_blank"
            class="text-n-brand"
          >
            {{ element.content.value }}
          </a>
        </ContactDetailsItem>
      </template>
    </CustomAttributes>
  </div>
</template>
